import { useState } from 'react';
import { Icon } from '@/components/common/NavIcons';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import {
  CHARGE_STATUS_LABEL, CHARGE_STATUS_TONE, dateLabel, INVOICE_STATUS_LABEL, INVOICE_STATUS_TONE,
  PAYMENT_STATUS_LABEL, PAYMENT_STATUS_TONE, periodLabel, PROVENANCE_HINT, PROVENANCE_LABEL,
  PROVENANCE_TONE, startOfMonth, today,
  type ChargeStatus, type InvoiceStatus, type PaymentStatus, type Provenance,
} from '@cleanblue/shared';
import { api } from '@/lib/api';
import { useApi, useApiMutation } from '@/lib/hooks';
import { haptic } from '@/lib/haptics'; // HAPTIC_FEEDBACK
import {
  Async, Button, Card, Cell, Empty, Field, Modal, Money, Note, PageHead, StatusBadge, Table,
} from '@/components/ui';

interface Charge {
  id: string; unit_code: string; unit_name: string | null; period_start: string;
  service_name_snapshot: string; total_minor: number; paid_minor: number;
  status: ChargeStatus; payment_rollup: PaymentStatus; provenance: Provenance;
  payer_count: number; due_date: string;
}

export function ChargesPage() {
  const [params, setParams] = useSearchParams();
  const period = params.get('period') ?? startOfMonth(today()).slice(0, 7);
  const query = useApi<{ charges: Charge[] }>(`/admin/charges?period=${period}`);

  return (
    <>
      <PageHead
        title="Cobros"
        subtitle="El evento económico del mes. Lo que cada persona debe está en Pagos."
        actions={
          <>
            <input
              className="input"
              type="month"
              value={period}
              onChange={(e) => setParams({ period: e.target.value })}
              style={{ width: 170 }}
            />
            <a className="btn" href={`/api/admin/export/receivables?period=${period}`}>Exportar CSV</a>
          </>
        }
      />

      <Async query={query} empty={(d) => d.charges.length === 0 && (
        <Empty
          icon="cobros"
          title={`No hay cobros de ${periodLabel(`${period}-01`)}`}
          hint="Puede que el mes todavía no se haya generado."
          action={<Link to="/admin/cobros/generar" className="btn btn--primary">Generar el mes</Link>}
        />
      )}>
        {(data) => (
          <Card flush>
            <Table
              head={
                <tr>
                  <th>Consultorio</th><th>Servicio</th><th className="right">Total</th>
                  <th className="right">Pagado</th><th>Cobro</th><th>Origen</th>
                </tr>
              }
              foot={
                <tr>
                  <td colSpan={2}>{data.charges.length} cobros</td>
                  <td className="right">
                    <Money value={data.charges.reduce((a, c) => a + c.total_minor, 0)} />
                  </td>
                  <td className="right">
                    <Money value={data.charges.reduce((a, c) => a + c.paid_minor, 0)} />
                  </td>
                  <td colSpan={2} />
                </tr>
              }
            >
              {data.charges.map((c) => (
                <tr key={c.id}>
                  <td className="strong">
                    <Link to={`/admin/cobros/detalle/${c.id}`}>
                      <Cell sub={c.unit_name}>{c.unit_code}</Cell>
                    </Link>
                  </td>
                  <td>
                    <Cell sub={`${c.payer_count} pagador${c.payer_count === 1 ? '' : 'es'}`}>
                      {c.service_name_snapshot}
                    </Cell>
                  </td>
                  <td className="right"><Money value={c.total_minor} /></td>
                  <td className="right"><Money value={c.paid_minor} dim={c.paid_minor === 0} /></td>
                  <td>
                    {c.status === 'VOID'
                      ? <StatusBadge value={c.status} labels={CHARGE_STATUS_LABEL} tones={CHARGE_STATUS_TONE} />
                      : <StatusBadge value={c.payment_rollup} labels={PAYMENT_STATUS_LABEL} tones={PAYMENT_STATUS_TONE} />}
                  </td>
                  <td>
                    <StatusBadge
                      value={c.provenance}
                      labels={PROVENANCE_LABEL}
                      tones={PROVENANCE_TONE}
                      hints={PROVENANCE_HINT}
                    />
                  </td>
                </tr>
              ))}
            </Table>
          </Card>
        )}
      </Async>
    </>
  );
}

/* ── Detalle ──────────────────────────────────────────────────────────────── */

interface ChargeDetail {
  charge: Charge & {
    subtotal_minor: number; discount_minor: number; tax_minor: number;
    tax_rate_snapshot: number; allocation_mode_snapshot: string; period_end: string;
    void_reason: string | null; provenance_note: string | null;
  };
  payers: {
    id: string; billing_name_snapshot: string; billing_taxid_snapshot: string | null;
    share_basis: number | null; share_kind: string | null;
    due_minor: number; paid_minor: number; due_date: string;
    payment_status: PaymentStatus; invoice_status: InvoiceStatus; open_submissions: number;
  }[];
  payments: { id: string; amount_minor: number; paid_at: string; method: string; status: string }[];
}

export function ChargeDetailPage() {
  const { id } = useParams<{ id: string }>();
  const query = useApi<ChargeDetail>(`/admin/charges/${id}`);
  const [voiding, setVoiding] = useState(false);

  return (
    <Async query={query}>
      {({ charge: c, payers, payments }) => (
        <>
          <PageHead
            title={`${c.unit_code} · ${periodLabel(c.period_start)}`}
            subtitle={
              <>
                {c.service_name_snapshot} · vence el {dateLabel(c.due_date)}
                {' · '}
                <StatusBadge
                  value={c.provenance}
                  labels={PROVENANCE_LABEL}
                  tones={PROVENANCE_TONE}
                  hints={PROVENANCE_HINT}
                />
              </>
            }
            actions={
              <>
                {c.status !== 'VOID' && (
                  <Button variant="danger" onClick={() => setVoiding(true)}>Anular</Button>
                )}
                <Link to="/admin/cobros" className="btn" title="Volver" aria-label="Volver a Cobros"><Icon name="volver" /></Link>
              </>
            }
          />

          {c.status === 'VOID' && (
            <Note tone="danger">Cobro anulado. Motivo: {c.void_reason ?? 'sin especificar'}</Note>
          )}

          <div className="grid grid--sidebar">
            <Card title="Cuentas por cobrar" flush>
              <Table head={
                <tr>
                  <th>Pagador</th><th className="right">Parte</th><th className="right">Debe</th>
                  <th>Pago</th><th>Factura</th>
                </tr>
              }>
                {payers.map((p) => (
                  <tr key={p.id}>
                    <td className="strong">
                      <Cell sub={p.billing_taxid_snapshot ?? 'sin cédula'}>
                        {p.billing_name_snapshot}
                      </Cell>
                    </td>
                    <td className="right small muted">
                      {p.share_kind === 'PERCENT' && p.share_basis != null
                        ? `${p.share_basis}%`
                        : p.share_basis != null ? <Money value={p.share_basis} /> : '—'}
                    </td>
                    <td className="right">
                      <Cell sub={p.paid_minor > 0 ? <>pagó <Money value={p.paid_minor} /></> : undefined}>
                        <Money value={p.due_minor} />
                      </Cell>
                    </td>
                    <td>
                      <StatusBadge value={p.payment_status} labels={PAYMENT_STATUS_LABEL} tones={PAYMENT_STATUS_TONE} />
                      {p.open_submissions > 0 && <span className="tbl__sub">comprobante en revisión</span>}
                    </td>
                    <td>
                      <StatusBadge value={p.invoice_status} labels={INVOICE_STATUS_LABEL} tones={INVOICE_STATUS_TONE} />
                    </td>
                  </tr>
                ))}
              </Table>
            </Card>

            <div style={{ display: 'grid', gap: 14 }}>
              <Card title="El cálculo del mes">
                {/* Congelado al emitir: subir el precio del servicio no toca esto. */}
                <dl className="dl">
                  <dt>Subtotal</dt><dd><Money value={c.subtotal_minor} /></dd>
                  <dt>Descuento</dt><dd><Money value={-c.discount_minor} /></dd>
                  <dt>IVA {(c.tax_rate_snapshot * 100).toFixed(0)}%</dt><dd><Money value={c.tax_minor} /></dd>
                  <dt style={{ fontWeight: 600, color: 'var(--ink)' }}>Total</dt>
                  <dd><Money value={c.total_minor} size="lg" /></dd>
                </dl>
                <p className="small muted mt-2">
                  Estos montos quedaron congelados el día que se emitió el cobro.
                </p>
              </Card>

              {payments.length > 0 && (
                <Card title="Pagos" flush>
                  <Table head={<tr><th>Fecha</th><th className="right">Monto</th></tr>}>
                    {payments.map((p) => (
                      <tr key={p.id} style={p.status === 'REVERSED' ? { opacity: 0.5 } : undefined}>
                        <td className="small">
                          <Cell sub={p.status === 'REVERSED' ? 'reversado' : p.method}>
                            {p.paid_at.slice(0, 10)}
                          </Cell>
                        </td>
                        <td className="right"><Money value={p.amount_minor} /></td>
                      </tr>
                    ))}
                  </Table>
                </Card>
              )}
            </div>
          </div>

          {voiding && <VoidModal chargeId={c.id} onClose={() => setVoiding(false)} />}
        </>
      )}
    </Async>
  );
}

function VoidModal({ chargeId, onClose }: { chargeId: string; onClose: () => void }) {
  const [reason, setReason] = useState('');
  const mutation = useApiMutation(
    () => api.post(`/admin/charges/${chargeId}/void`, { reason }),
    ['/admin/charges', '/admin/dashboard', '/admin/receivables'],
    { onSuccess: () => { haptic.warning(); onClose(); } }, // HAPTIC_FEEDBACK
  );

  return (
    <Modal
      title="Anular el cobro"
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>Cancelar</Button>
          <Button variant="danger" disabled={reason.length < 3 || mutation.isPending}
            onClick={() => mutation.mutate()}>
            {mutation.isPending ? 'Anulando…' : 'Anular'}
          </Button>
        </>
      }
    >
      {mutation.error && <Note tone="danger">{mutation.error.message}</Note>}
      <Note tone="warn">
        Anular no borra nada: el cobro queda marcado y el período vuelve a quedar libre para
        generarlo de nuevo. Si ya tiene un pago confirmado, hay que reintegrarlo primero.
      </Note>
      <div className="mt-2">
        <Field label="Motivo" required hint="Queda en la bitácora.">
          <textarea className="input" rows={3} value={reason} onChange={(e) => setReason(e.target.value)} />
        </Field>
      </div>
    </Modal>
  );
}
